import type { AgreementSignedContent } from 'commonTypes/api/scenarios/AgreementSigned';
import type { IAgreementSigningContent } from 'commonTypes/api/scenarios/AgreementSigning';
import type { CardDeliveryInviteContent } from 'commonTypes/api/scenarios/CardDeliveryInviteContent';
import type { ClientRefusedContent } from 'commonTypes/api/scenarios/ClientRefusedContent';
import type {
  ConditionsSelectionContent,
  ConditionsSelectionInteractions,
} from 'commonTypes/api/scenarios/ConditionsSelection';
import type {
  TCreditSelectionScreenContent,
  TCreditSelectionScreenInteractions,
  TCreditSelectionScreenRules,
  TCreditSelectionStepConfig,
} from 'commonTypes/api/scenarios/CreditSelectionScreenContent';
import type { ErrorStepContent } from 'commonTypes/api/scenarios/ErrorStep';
import type { FraudPreventionContent } from 'commonTypes/api/scenarios/FraudPreventionContent';
import type { FraudRiskDetectedContent } from 'commonTypes/api/scenarios/FraudRiskDetectedContent';
import type {
  OfferCoolingPeriodContent,
  OfferCoolingPeriodInteractions,
} from 'commonTypes/api/scenarios/OfferCoolingPeriod';
import type {
  OfferDisbursementContent,
  OfferDisbursementInteractions,
} from 'commonTypes/api/scenarios/OfferDisbursement';
import type { SelfRestrictedContent } from 'commonTypes/api/scenarios/SelfRestrictedContent';

import type { BaseScenario, SCENARIO_STEP } from './BaseTypes';
import type { DocumentsLoadingContent, DocumentsLoadingInteractions } from './DocumentsLoading';
import type { OfferApprovalContent } from './OfferApproval';

/**
 * Дженерики дают уникальный обьект из content для кажого типа сценраия
 */
export type StepOfType<T extends SCENARIO_STEP> = Extract<IScenario, { step: T }>;
export type ContentOfType<T extends SCENARIO_STEP> = StepOfType<T>['content'];

export const getParticularStepContent = <T extends SCENARIO_STEP = SCENARIO_STEP>(
  stepData: IScenario | undefined | null,
  step: T,
): ContentOfType<T> | null => {
  return stepData?.step === step ? (stepData?.content as unknown as ContentOfType<T> | null) : null;
};

export type IScenario =
  | BaseScenario<
      SCENARIO_STEP.CONDITIONS_SELECTION,
      ConditionsSelectionContent,
      ConditionsSelectionInteractions,
      unknown
    >
  | BaseScenario<SCENARIO_STEP.CREDIT_OFFERS_LOADING, unknown, unknown, unknown>
  | (BaseScenario<
      SCENARIO_STEP.CREDIT_SELECTION,
      TCreditSelectionScreenContent,
      TCreditSelectionScreenInteractions,
      TCreditSelectionScreenRules
    > & {
      stepConfig?: TCreditSelectionStepConfig;
    })
  | BaseScenario<SCENARIO_STEP.OFFER_DISBURSEMENT, OfferDisbursementContent, OfferDisbursementInteractions, unknown>
  | BaseScenario<SCENARIO_STEP.DOCUMENTS_LOADING, DocumentsLoadingContent, DocumentsLoadingInteractions, unknown>
  | BaseScenario<SCENARIO_STEP.GO_TO_LEADGEN, unknown, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.OFFER_APPROVAL, OfferApprovalContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.AGREEMENT_SIGNING, IAgreementSigningContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.OFFER_COOLING_PERIOD, OfferCoolingPeriodContent, OfferCoolingPeriodInteractions, unknown>
  | BaseScenario<SCENARIO_STEP.FRAUD_RISK_DETECTED, FraudRiskDetectedContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.CARD_DELIVERY_INVITE, CardDeliveryInviteContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.ERROR_STEP, ErrorStepContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.FRAUD_PREVENTION, FraudPreventionContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.CLIENT_REFUSED, ClientRefusedContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.CREDIT_SELF_RESTRICTION, SelfRestrictedContent, unknown, unknown>
  | BaseScenario<SCENARIO_STEP.AGREEMENT_SIGNED, AgreementSignedContent, unknown, unknown>;

export type INextScenarioResponse = IScenario;
